import { useState, useEffect, useRef, useCallback, useMemo } from "react";

type OpenPanelsMap = Record<string, boolean>;

const shallowEqual = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const useShallowStableArray = (arr: string[]) => {
  const ref = useRef<string[]>(arr);
  if (!shallowEqual(ref.current, arr)) {
    ref.current = arr;
  }
  return ref.current;
};

export const useOpenPanels = (
  panelIdsInput: string[],
  defaultCollapsedIdsInput: string[]
): OpenPanels => {
  const panelIds = useShallowStableArray(panelIdsInput);
  const defaultCollapsedIds = useShallowStableArray(defaultCollapsedIdsInput);

  const getInitial = useCallback(
    (panelIds_: string[], defaultCollapsedIds_: string[]): OpenPanelsMap => {
      const collapsed = new Set(defaultCollapsedIds_);
      return Object.fromEntries(
        panelIds_.map((id) => [id, !collapsed.has(id)])
      ) as OpenPanelsMap;
    },
    []
  );

  const [openPanelsMap, setOpenPanelsMap] = useState<OpenPanelsMap>(() =>
    getInitial(panelIds, defaultCollapsedIds)
  );

  const mapRef = useRef(openPanelsMap);
  const panelIdsRef = useRef(panelIds);
  const defaultCollapsedRef = useRef(defaultCollapsedIds);

  useEffect(() => {
    mapRef.current = openPanelsMap;
  }, [openPanelsMap]);

  useEffect(() => {
    panelIdsRef.current = panelIds;
  }, [panelIds]);

  useEffect(() => {
    defaultCollapsedRef.current = defaultCollapsedIds;
  }, [defaultCollapsedIds]);

  useEffect(() => {
    const initial = getInitial(panelIds, defaultCollapsedIds);
    setOpenPanelsMap((prev) => {
      let changed = false;
      const next: OpenPanelsMap = { ...prev };
      for (const id of Object.keys(initial)) {
        if (!(id in next)) {
          next[id] = initial[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [panelIds, defaultCollapsedIds, getInitial]);

  const getValueById = useCallback(
    (id: string): boolean => !!openPanelsMap[id],
    [openPanelsMap]
  );

  const setValueById = useCallback((id: string, value: boolean): void => {
    setOpenPanelsMap((prev) => {
      if (prev[id] === value) return prev;
      return { ...prev, [id]: value };
    });
  }, []);

  const reset = useCallback(() => {
    setOpenPanelsMap(
      getInitial(panelIdsRef.current, defaultCollapsedRef.current)
    );
  }, [getInitial]);

  return useMemo(
    () => ({
      openPanelsMap,
      getValueById,
      setValueById,
      reset,
    }),
    [openPanelsMap, getValueById, setValueById, reset]
  );
};

export type OpenPanels = {
  openPanelsMap: Readonly<OpenPanelsMap>;
  getValueById: (id: string) => boolean;
  setValueById: (id: string, value: boolean) => void;
  reset: () => void;
};
